// @ts-nocheck
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects'
import { sanitizeTelephone } from '../../helpers'
import { appsLoginSelected } from '../../helpers/app'
import { urls } from '../../routing'
import { AuthenticationService, LiteralsService, UserService } from '../../services'
import {
  checkSessionAction,
  clearSignInFieldsAction,
  clearSignUpFieldsAction,
  clearVerificationFieldsAction,
  setIsSigningInAction,
  setNewPasswordRequiredUserAction,
  setNotificationAction,
  setUserInfoAction,
  setVerificationDNIAction,
  setVerificationEmailAction,
  setVerificationOriginAction,
  signInFailedAction,
  signInNewPasswordRequiredAction,
  signInSuccessfulAction
} from '../actions'
import { initialUserInfoState } from '../reducers/user-info'
import { IStoreState } from '../states'
import { SignInTypeKeys } from '../types'
import { Application } from '../../config'

const { isIframe } = Application

function deriveCognitoUsername(loginInput: string): string | null {
  const trimmed = (loginInput || '').trim()
  if (!trimmed) return null
  return trimmed.includes('@') ? trimmed.split('@')[0] : trimmed
}

function* tryResendForUnverifiedAlias(): any {
  const routerHistory = yield getContext('routerHistory')

  const { username } = yield select((state: IStoreState) => state.signIn)
  const cognitoUsername = deriveCognitoUsername(username)
  if (!cognitoUsername) return false
  try {
    yield call(AuthenticationService.resendSignUpConfirmationCode, cognitoUsername)
  } catch (resendError: any) {
    return false
  }
  const trimmed: string = (username || '').trim()
  yield put(setVerificationEmailAction(trimmed))
  yield put(setVerificationDNIAction(cognitoUsername))
  yield put(setVerificationOriginAction('signIn'))
  yield put(setNotificationAction({
    isError: false,
    label: LiteralsService.get('verificationCodeSent', true)
  }))
  yield put(signInSuccessfulAction())
  yield call(routerHistory.push, urls.accountVerificationStep)
  return true
}

function* handleUnconfirmedUser(): any {
  const routerHistory = yield getContext('routerHistory')

  const { username } = yield select((state: IStoreState) => state.signIn)
  const trimmed: string = (username || '').trim()
  yield put(setVerificationEmailAction(trimmed))
  yield put(setVerificationDNIAction(trimmed.split('@')[0]))

  try {
    yield call(AuthenticationService.resendSignUpConfirmationCode, trimmed.split('@')[0])
    yield put(setNotificationAction({
      isError: false,
      label: LiteralsService.get('verificationCodeSent', true)
    }))
  } catch (resendError: any) {
    yield put(setNotificationAction({
      isError: true,
      label: LiteralsService.get('UserNotConfirmedException', true),
    }))
  }
  yield put(setVerificationOriginAction('signIn'))
  yield put(signInSuccessfulAction())
  yield call(routerHistory.push, urls.accountVerificationStep)
}

// Worker Saga
function* fetchSignInAsync() : any {
  try {
    yield put(setIsSigningInAction())
    const { username, password, callingCode } = yield select(
      (state: IStoreState) => state.signIn
    )
    const isPhoneLogin = !!appsLoginSelected?.login.includes('phone') && !appsLoginSelected?.login.includes('email')
    const cognitoUsername = isPhoneLogin ? sanitizeTelephone(username.trim(), callingCode) : username
    const user = yield call(AuthenticationService.signIn, { username: cognitoUsername, password })
    if (user && user.challengeName === 'NEW_PASSWORD_REQUIRED') {
      yield put(signInNewPasswordRequiredAction(true))
      yield put(setNewPasswordRequiredUserAction(user))
      yield put(signInSuccessfulAction())
    } else {
      yield put(checkSessionAction())
    }
  } catch (error: any) {
    const errorName = error?.name || error?.code || error?.underlyingError?.name
    if (errorName === 'UserNotConfirmedException') {
      yield* handleUnconfirmedUser()
      return
    }
    if (errorName === 'UserNotFoundException') {
      const recovered: boolean = yield* tryResendForUnverifiedAlias()
      if (recovered) return
    }
    yield put(signInFailedAction(error))
  }
}

function* signOutAsync() : any {
  const routerHistory = yield getContext('routerHistory')

  try {
    try {
      yield call(UserService.signOutDevice)
    } catch (error) {
      // tslint:disable-next-line: no-console
      console.error(error)
    }
    yield call(AuthenticationService.signOut)
  } catch (error) {
    // tslint:disable-next-line: no-console
    console.error(error)
  }

  yield put(clearSignInFieldsAction())
  yield put(clearSignUpFieldsAction())
  yield put(clearVerificationFieldsAction())
  yield put(setUserInfoAction(initialUserInfoState))

  if (isIframe) {
    let townId = new URLSearchParams(location.search).get('townId')
    if (!townId) townId = routerHistory.location.state?.townId
    yield call(routerHistory.push, urls.newReservationBusStopsStep + `?townId=${townId}`)
  }
  else yield call(routerHistory.replace, urls.signIn)
}

function* signOutWihoutUserCredentialsAsync() : any {
  const routerHistory = yield getContext('routerHistory')

  try {
    yield call(AuthenticationService.signOut)
  } catch (error) {
    console.error(error)
  }
  yield put(clearSignInFieldsAction())
  yield put(clearSignUpFieldsAction())
  yield put(clearVerificationFieldsAction())
  yield put(setUserInfoAction(initialUserInfoState))
  yield call(routerHistory.push, urls.signIn)
}

// Watcher Saga:
function* watchFetchSignInAsync() {
  yield takeLatest(SignInTypeKeys.SIGN_IN, fetchSignInAsync)
}

function* watchSignOutAsync() {
  yield takeLatest(SignInTypeKeys.SIGN_OUT, signOutAsync)
}

function* watchSsignOutAsyncWihoutUserCredentials() {
  yield takeLatest(SignInTypeKeys.SIGN_OUT_WITHOUT_USER_CREDENTIALS, signOutWihoutUserCredentialsAsync)
}

// single entry point to start all Sagas at once
export default function* rootSaga() {
  yield all([watchFetchSignInAsync(), watchSignOutAsync(), watchSsignOutAsyncWihoutUserCredentials()])
}
